import { useState, useEffect, useRef } from 'react';
import styles from '../../style/styles/ProductOption.module.css';
import { OptionDropdown } from './OptionDropdown';

export const OptionSelectedProduct = (props) => {
    const {
        combination,
        productName,
        noOptionText = false,   // true: 기본, false: 선택 옵션 추가
        optionValueList = [],
        optionCodeList = [],
        optionValueNoList = [],
        productOptions = [],
        existingTextOptions = [],
        quantity,
        setQuantity,
        price,
        additionalPrice = 0,
        processProductDeleteClicked,
        onTextOptionChange,
        setToastMessage,
        setIsToastVisible,
    } = props;

    // Initialize textOptions: use existingTextOptions if available, otherwise filter from productOptions
    const getInitialTextOptions = () => {
        if (existingTextOptions && existingTextOptions.length > 0) {
            return existingTextOptions;
        }
        return productOptions
            .filter(option => option.isTextOption)
            .map(option => ({
                optionName: option.optionName,
                selectedValue: option.selectedValue || '',
            }));
    };
    
    const [textOptions, setTextOptions] = useState(getInitialTextOptions());
    const prevTextOptionsRef = useRef(textOptions);
    
    // Update textOptions when existingTextOptions or productOptions change
    // 무한 루프 방지를 위해 깊은 비교 또는 이전 값과 비교
    useEffect(() => {
        // existingTextOptions가 있고 길이가 0보다 크면 사용
        if (existingTextOptions && existingTextOptions.length > 0) {
            // 이전 값과 비교하여 실제로 변경된 경우에만 업데이트
            const hasChanged = JSON.stringify(existingTextOptions) !== JSON.stringify(prevTextOptionsRef.current);
            if (hasChanged) {
                setTextOptions(existingTextOptions);
                prevTextOptionsRef.current = existingTextOptions;
            }
        } else {
            // Initialize from productOptions if no existing textOptions
            // productOptions에서 textOption만 필터링
            const newTextOptions = productOptions
                .filter(option => option.isTextOption)
                .map(option => ({
                    optionName: option.optionName,
                    selectedValue: option.selectedValue || '',
                }));
            
            // 이전 값과 비교하여 실제로 변경된 경우에만 업데이트
            const hasChanged = JSON.stringify(newTextOptions) !== JSON.stringify(prevTextOptionsRef.current);
            if (hasChanged) {
                setTextOptions(newTextOptions);
                prevTextOptionsRef.current = newTextOptions;
            }
        }
    }, [productOptions, existingTextOptions]);
    
    // textOptions가 변경될 때 ref 업데이트
    useEffect(() => {
        prevTextOptionsRef.current = textOptions;
    }, [textOptions]);

    const optionsConcatText = optionValueList.join(' - ');
    const [totalPrice, setTotalPrice] = useState((price + additionalPrice) * quantity);

    useEffect(() => {
        setTotalPrice((price + additionalPrice) * quantity);
    }, [price, additionalPrice, quantity])

    const handleOptionTextChange = (optionName, value) => {
        // text limit check
        const currentTextOption = textOptions.find(option => option.optionName === optionName);
        if (currentTextOption?.textLengthLimit && value?.length > currentTextOption?.textLengthLimit) {
            setToastMessage('최대 글자수를 초과했어요');
            setIsToastVisible(true);
            return;
        }

        const updatedTextOptions = textOptions.map((option) => 
            option.optionName === optionName 
                ? { ...option, selectedValue: value || '' }
                : option
        );
        setTextOptions(updatedTextOptions);
        // Update parent's optionSelectedProductList
        if (onTextOptionChange) {
            onTextOptionChange(updatedTextOptions);
        }
    }

    const handleOptionTextFocus = (optionName) => {
        setTextOptions(prev => 
            prev.map((option) => 
                option.optionName === optionName 
                    ? { ...option, isFocused: true }
                    : option
            )
        );
    }

    const handleOptionTextBlur = (optionName) => {
        setTextOptions(prev => 
            prev.map((option) => 
                option.optionName === optionName 
                    ? { ...option, isFocused: false }
                    : option
            )
        );
    }

    const handleLinkedTypeUnrequiredOptionSelect = (optionName, idx) => {
        const updatedTextOptions = textOptions.map((option) => 
            option.optionName === optionName 
                ? { 
                    ...option, 
                    selectedIndex: idx,
                    selectedValue: option.value[idx]?.optionText || '---'
                }
                : option
        );
        setTextOptions(updatedTextOptions);
        // Update parent's optionSelectedProductList
        if (onTextOptionChange) {
            onTextOptionChange(updatedTextOptions);
        }
    }

    return (
        <div className={styles.OptionSelectedProduct__Container}>
            <div className={styles.OptionSelectedProduct__Wrapper}>
                <div className='spacing-12'/>
                <div className={styles.OptionSelectedProduct__Head}>
                    <div className={styles.OptionSelectedProduct__Head__Text}>
                        <p className='h8 fc-gray-800'>
                            {productName}
                        </p>
                        {
                            noOptionText ? null :
                            <>
                            <div className='spacing-4'/>
                            <ul className='h9 fc-gray-500' style={{ margin: 0, padding: 0, listStyle: 'disc', paddingLeft: '20px'}}>
                                <li>
                                    {optionsConcatText} {!noOptionText && additionalPrice !== 0 && `(${additionalPrice > 0 ? '+' : ''}${additionalPrice?.toLocaleString()}원)`}
                                </li>
                            </ul>
                            </>
                        }
                    </div>
                    {
                        combination !== 'default' ? 
                        <img 
                            src='/img/units/close-gray400.png' 
                            width={20} 
                            height={20} 
                            alt='' 
                            onClick={processProductDeleteClicked}
                        /> : null
                    }
                </div>
                <div className='spacing-12'/>
                <div className={styles.OptionSelectedProduct__Bottom}>
                    <div className={styles.OptionSelectedProduct__Bottom__QuantityControl}>
                        <img 
                            src={`${quantity > 1 
                                ? '/img/units/minus-button-active.png' 
                                : '/img/units/minus-button-disabled.png'}`
                            }
                            width={28} 
                            height={28} 
                            alt='' 
                            onClick={() => {
                                if (quantity > 1) {
                                    setQuantity(quantity - 1)
                                }
                            }}
                        />
                        <p className='h9 fc-gray-800'>{quantity}</p>
                        <img 
                            src={'/img/units/add-button-active.png'}
                            width={28} 
                            height={28} 
                            alt='' 
                            onClick={() => {
                                setQuantity(quantity + 1)
                            }}
                        />
                    </div>
                    <p className='h8 fc-gray-800'>
                        {totalPrice?.toLocaleString()}원
                    </p>
                </div>
            </div>
            <div className='spacing-16'/>
            {
                (!noOptionText || combination === 'default') ?
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', height: 'fit-content'}}>
                    {/* 상품 연동형 옵션의 경우에만 [선택] 옵션을 textOption 처럼 취급 */}
                    {textOptions
                        .filter(textOption => (!textOption.isTextOption && !textOption.isFileOption && !textOption.required))
                        .map((linkedTypeUnrequiredOption) => (
                            <div key={linkedTypeUnrequiredOption.optionName} style={{ display: 'flex', alignItems: 'center'}}>
                                <p className={`h9 fc-gray-600 ${styles.ProductOption__Content__OptionName}`}>
                                    {linkedTypeUnrequiredOption.optionName}
                                    <span className='fc-warning'>
                                        {linkedTypeUnrequiredOption.required ? '*' : ''}
                                    </span>
                                </p>
                                <div className='spacing-8'/>
                                <OptionDropdown 
                                    options={linkedTypeUnrequiredOption.value}
                                    isLargeType={linkedTypeUnrequiredOption.displayType === 'P' || linkedTypeUnrequiredOption.value.some(value => value.optionColor !== null)}
                                    selectedOption={linkedTypeUnrequiredOption.selectedIndex}
                                    setSelectedOption={(idx) => handleLinkedTypeUnrequiredOptionSelect(linkedTypeUnrequiredOption.optionName, idx)}
                                    placeholder={true}
                                    placeholderText={`[${linkedTypeUnrequiredOption.required ? '필수' : '선택'}] 옵션을 선택해주세요`}
                                    setToastMessage={setToastMessage}
                                    setIsToastVisible={setIsToastVisible}
                                />
                            </div>
                        ))
                    }
                    {textOptions.filter(textOption => textOption.isTextOption).map((textOption) => (
                        <div key={textOption.optionName} style={{ display: 'flex', alignItems: 'center'}}>
                            <p className={`h9 fc-gray-600 ${styles.ProductOption__Content__OptionName}`}>
                                {textOption.optionName}
                                <span className='fc-warning'>
                                    {textOption.required ? '*' : ''}
                                </span>
                            </p>
                            <div className='spacing-8'/>
                            <input 
                                id={textOption.optionName}
                                className={styles.ProductOption__Content__OptionTextInput}
                                type='text' 
                                value={textOption.selectedValue || ''} 
                                onChange={(e) => handleOptionTextChange(textOption.optionName, e.target.value)}
                                onFocus={() => handleOptionTextFocus(textOption.optionName)}
                                onBlur={() => handleOptionTextBlur(textOption.optionName)}
                                placeholder={`[${textOption.required ? '필수' : '선택'}] 옵션을 입력해주세요`}
                            />
                            {
                                textOption?.textLengthLimit 
                                && 
                                <>
                                <div className='spacing-8'/>
                                <p className='h9' style={{ width: '40px', textAlign: 'right'}}>
                                    <span className='h9 fc-gray-600'>(</span>
                                    <span className='h9 fc-gray-800'>{textOption.selectedValue?.length}</span> 
                                    <span className='h9 fc-gray-600'>/{textOption.textLengthLimit})</span>
                                </p>
                                </>
                            }
                        </div>
                    ))
                    }
                </div> : null
            }
        </div>
    )
}